"use client";
import { Fragment } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AppButton, Container, SectionSubHeading } from "@/components";
import { caseStudyCloseFacts } from "@/data/CaseStudyPageData";
import { fadeInUp, word, wordStagger } from "@/lib/animation/variant";

const HEADLINE = ["Say", "the", "number.", "We", "will", "say"];
// The question and the answer belong on their own lines once there is room.
const HEADLINE_BREAK_AFTER = 2;

const CaseStudyClose = () => {
  return (
    <section className="w-full bg-background pt-16 pb-16 md:pt-24 md:pb-24">
      <Container className="grid gap-8 px-4 lg:grid-cols-[1.24fr_0.76fr] lg:items-end lg:gap-15 lg:px-10">
        <div className="space-y-2">
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionSubHeading subtitle="Next step" />
          </motion.div>

          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={wordStagger}
            className="flex flex-wrap gap-x-[0.22em] text-5xl leading-[0.85] font-black tracking-tighter uppercase md:text-7xl xl:text-8xl"
          >
            {HEADLINE.map((item, index) => (
              <Fragment key={`${item}-${index}`}>
                <span className="inline-block overflow-hidden">
                  <motion.span variants={word} className="inline-block">
                    {item}
                  </motion.span>
                </span>
                {index === HEADLINE_BREAK_AFTER && (
                  <span aria-hidden className="hidden basis-full md:block" />
                )}
              </Fragment>
            ))}
            <span className="inline-block overflow-hidden">
              <motion.span
                variants={word}
                className="inline-block text-primary"
              >
                the route.
              </motion.span>
            </span>
          </motion.h2>

          <motion.p
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="max-w-[56ch] pt-4 leading-loose font-medium text-muted-foreground"
          >
            You do not need to know which service you need. Describe where the
            business is stuck and we will send back the shortest route to fixing
            it — including the parts you can skip and the parts you do not need
            us for.
          </motion.p>

          <motion.div
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="pt-4"
          >
            <AppButton
              href="/contact"
              buttonClass="px-8 py-4 text-base"
              icon={<ArrowUpRight className="size-5" />}
            >
              Start a project
            </AppButton>
          </motion.div>
        </div>

        <motion.div
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <dl className="grid gap-4">
            {caseStudyCloseFacts.map((fact) => (
              <div key={fact.label} className="border-l-4 border-primary pl-4">
                <dt className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
                  {fact.label}
                </dt>
                <dd className="mt-1.5 text-xl leading-none font-black tracking-tight uppercase">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>

          {/* Said plainly, because a conversion figure alongside paid media is
              a credit, not a claim of causation. */}
          <p className="mt-6 border-t border-black/10 pt-4 font-mono text-[9px] leading-loose tracking-[0.18em] text-muted-foreground uppercase">
            Attribution: paid media running, on-site conversion credited — not
            raw revenue
          </p>
        </motion.div>
      </Container>
    </section>
  );
};

export default CaseStudyClose;
